import React, {useState} from "react";
import PropTypes from "prop-types";
import {useQuery} from "@apollo/client";
import {Box, Button, FormControl, InputLabel, MenuItem, Select, Typography} from "@mui/material";
import {GET_PLAYBOOKS_CORE} from "../../../graphql/playbookQueries";
import {ALERT} from "../../../resources/environment";
import {useChatbotMessageStyles} from "../useChatbotMessageStyles";
import {PLAYBOOK_WIDGET_TOOL_NAME} from "../../agent/useAgentListTools/toolNames";
import {find, isEmpty, map} from "underscore";

export const submitPlaybookWidget = (
  value,
  label,
  {payload, navigate, pathname, setPendingAddPlaybook, setPendingWidgetMessage},
) => {
  const noPlaybookSelected = value === "none";
  setPendingAddPlaybook(noPlaybookSelected ? "No playbook" : value);
  setPendingWidgetMessage({
    text: noPlaybookSelected ?
      "I'm creating this without a playbook and I'm on the Add form now, ready to fill it out." :
      `I've selected playbook ${label} and I'm on the Add form now; it has pre-filled the ` +
      "playbook's values.",
    widgetType: PLAYBOOK_WIDGET_TOOL_NAME,
    widgetValue: value,
    widgetLabel: label,
    awaitRoute: payload.addRoute,
    sourceRoute: pathname,
  });
  navigate(payload.addRoute);
};

const PlaybookSelectorWidget = ({disabled = false, value, widgetLabel, payload, onSubmit}) => {
  const {classes, cx} = useChatbotMessageStyles();
  const [playbookInput, setPlaybookInput] = useState(null);

  const {data, loading} = useQuery(GET_PLAYBOOKS_CORE, {
    fetchPolicy: "network-only",
    variables: {approved: true, archived: false, disabled: false},
    skip: disabled,
    onError: (error) => {
      console.error(error);
      ALERT.error("Could not load playbooks, please try again");
    },
  });
  const playbooks = data?.playbooks || [];

  if (disabled) {
    return (
      <>
        <Typography variant="caption" className={classes.textHuman} display="block">
          Playbook selection
        </Typography>
        <Typography variant="body2" className={classes.textHuman}>
          {widgetLabel || value}
        </Typography>
      </>
    );
  }

  const noApprovedPlaybooks = !loading && isEmpty(playbooks);

  const suggestedId = payload?.suggestedPlaybookId;
  const suggestedPlaybook =
    suggestedId && find(playbooks, (playbook) => String(playbook.id) === String(suggestedId));
  const selected = playbookInput ?? (suggestedPlaybook ? suggestedPlaybook.id : "");

  return (
    <Box className={cx(classes.messageBox, classes.messageBoxHuman, classes.widgetBox)}>
      <Typography variant="body2" className={classes.textHuman} gutterBottom>
        Start from a playbook?
      </Typography>
      <Box className={classes.widgetSelectPanel}>
        <FormControl fullWidth disabled={noApprovedPlaybooks} variant="standard">
          <InputLabel id="widget-select-playbook-label">
            <Typography noWrap display="inline" variant="body1">
              {noApprovedPlaybooks ? "There are no approved Playbooks" : "Choose Playbook"}
            </Typography>
          </InputLabel>
          <Select
            labelId="widget-select-playbook-label"
            value={selected}
            onChange={(_, {props: {value}}) => setPlaybookInput(value)}
            inputProps={{"aria-label": "Select Playbook", id: "widget-select-playbook-input"}}
            label="Playbook"
            variant="standard"
          >
            {map(playbooks, (playbook, key) => (
              <MenuItem key={key} value={playbook.id}>
                {playbook.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <Box mt={1.5} display="flex" justifyContent="flex-end" gap={1}>
        <Button
          onClick={() =>
            onSubmit(selected, find(playbooks, (playbook) => playbook.id === selected)?.name)}
          variant="contained"
          size="small"
          disabled={!selected}
          className={classes.widgetUseButton}
        >
          Use
        </Button>
        <Button
          onClick={() => onSubmit("none", "No playbook")}
          size="small"
          className={classes.textHuman}
        >
          No playbook
        </Button>
      </Box>
    </Box>
  );
};

PlaybookSelectorWidget.propTypes = {
  disabled: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  widgetLabel: PropTypes.string,
  payload: PropTypes.shape({
    suggestedPlaybookId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
  onSubmit: PropTypes.func,
};

export default PlaybookSelectorWidget;
